import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  IconButton,
  Divider,
  Badge,
  CircularProgress,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { API_BASE_URL } from '../config/api';
import {
  Search as SearchIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';

interface Message {
  id: number;
  conversation_id: number;
  sender_id: number;
  sender_name: string;
  sender_role: string;
  content: string;
  message_type: string;
  attachment_url?: string;
  attachment_name?: string;
  attachment_type?: string;
  attachment_size?: number;
  latitude?: number;
  longitude?: number;
  location_name?: string;
  created_at: string;
  read: boolean;
}

interface Conversation {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  user_role: string;
  last_message: string;
  last_message_time: string;
  unread_count: number;
}

const Messages: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadConversations();
    // Poll for new messages every 5 seconds
    const interval = setInterval(() => {
      if (selectedConversation) {
        loadMessages(selectedConversation.id);
      }
      loadConversations();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/support/admin/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Only update if data has changed to prevent flicker
        setConversations(prev => {
          const newConvs = data.conversations || [];
          if (JSON.stringify(prev) !== JSON.stringify(newConvs)) {
            return newConvs;
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const loadMessages = async (conversationId: number) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/support/admin/conversations/${conversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        // Mark messages as read
        await markAsRead(conversationId);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (conversationId: number) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/support/admin/conversations/${conversationId}/read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      // Update conversation unread count
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    loadMessages(conversation.id);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      alert('File too large. Maximum size is 10MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain', 'application/zip', 'application/x-rar-compressed'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Allowed: images, PDF, Word, Excel, text, zip.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSelectedFile(file);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedFile) || !selectedConversation) return;

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      if (newMessage.trim()) {
        formData.append('content', newMessage);
      }
      
      if (selectedFile) {
        formData.append('attachment', selectedFile);
      }

      const response = await fetch(`${API_BASE_URL}/support/admin/conversations/${selectedConversation.id}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, data.message]);
        setNewMessage('');
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        loadConversations();
      } else {
        const errData = await response.json().catch(() => null);
        alert(errData?.message || `Failed to send message (${response.status})`);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setUploading(false);
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.user_email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalUnread = conversations.reduce((sum, conv) => sum + conv.unread_count, 0);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: 'background.default' }}>
      {/* Conversations List */}
      <Paper
        elevation={0}
        sx={{
          width: 380,
          borderRight: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Messages
          </Typography>

          {/* Stats */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Card sx={{ bgcolor: 'primary.50' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="caption" color="primary.main" fontWeight="medium">
                    Total Chats
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.dark">
                    {conversations.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card sx={{ bgcolor: 'warning.50' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="caption" color="warning.main" fontWeight="medium">
                    Unread
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.dark">
                    {totalUnread}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Conversations */}
        <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
          {filteredConversations.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <PersonIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">No conversations yet</Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                Conversations will appear when farmers or buyers send messages
              </Typography>
            </Box>
          ) : (
            filteredConversations.map((conversation) => (
              <React.Fragment key={conversation.id}>
                <ListItem
                  button
                  onClick={() => handleSelectConversation(conversation)}
                  selected={selectedConversation?.id === conversation.id}
                  sx={{
                    '&.Mui-selected': {
                      bgcolor: 'success.50',
                      '&:hover': { bgcolor: 'success.100' },
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      {conversation.user_name.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" fontWeight="600">
                          {conversation.user_name}
                        </Typography>
                        <Chip
                          label={conversation.user_role}
                          size="small"
                          color={conversation.user_role === 'farmer' ? 'success' : 'primary'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {conversation.last_message}
                      </Typography>
                    }
                  />
                  <Box sx={{ textAlign: 'right', ml: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(conversation.last_message_time)}
                    </Typography>
                    {conversation.unread_count > 0 && (
                      <Badge
                        badgeContent={conversation.unread_count}
                        color="warning"
                        sx={{ display: 'block', mt: 0.5 }}
                      />
                    )}
                  </Box>
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))
          )}
        </List>
      </Paper>

      {/* Chat Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  {selectedConversation.user_name.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="600">
                    {selectedConversation.user_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedConversation.user_email}
                  </Typography>
                </Box>
                <Chip
                  label={selectedConversation.user_role}
                  size="small"
                  color={selectedConversation.user_role === 'farmer' ? 'success' : 'primary'}
                />
              </Box>
              <IconButton>
                <MoreVertIcon />
              </IconButton>
            </Paper>

            {/* Messages */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 3,
                bgcolor: 'grey.50',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : messages.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <ChatIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography color="text.secondary">No messages yet</Typography>
                  <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                    Start the conversation!
                  </Typography>
                </Box>
              ) : (
                messages.map((message) => {
                  const isAdmin = message.sender_role === 'admin';
                  return (
                    <Box
                      key={message.id}
                      sx={{
                        display: 'flex',
                        justifyContent: isAdmin ? 'flex-end' : 'flex-start',
                      }}
                    >
                      <Paper
                        elevation={1}
                        sx={{
                          maxWidth: '60%',
                          p: 1.5,
                          bgcolor: isAdmin ? 'success.main' : 'background.paper',
                          color: isAdmin ? 'white' : 'text.primary',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography
                            variant="caption"
                            fontWeight="600"
                            sx={{ color: isAdmin ? 'success.50' : 'text.secondary' }}
                          >
                            {message.sender_name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: isAdmin ? 'success.100' : 'text.disabled' }}
                          >
                            {formatTime(message.created_at)}
                          </Typography>
                        </Box>
                        {message.content && (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: message.attachment_url ? 1 : 0 }}>
                            {message.content}
                          </Typography>
                        )}
                        {message.message_type === 'image' && message.attachment_url && (
                          <Box
                            component="img"
                            src={`${API_BASE_URL.replace('/api/v1', '')}${message.attachment_url}`}
                            alt={message.attachment_name}
                            sx={{ maxWidth: '100%', borderRadius: 1, mt: 0.5 }}
                          />
                        )}
                        {message.message_type === 'file' && message.attachment_url && (
                          <Box
                            component="a"
                            href={`${API_BASE_URL.replace('/api/v1', '')}${message.attachment_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              p: 1,
                              bgcolor: isAdmin ? 'success.dark' : 'grey.100',
                              borderRadius: 1,
                              mt: 0.5,
                              textDecoration: 'none',
                              color: 'inherit',
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.85 },
                            }}
                          >
                            <AttachFileIcon fontSize="small" />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" noWrap>
                                {message.attachment_name}
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                                {((message.attachment_size || 0) / 1024).toFixed(1)} KB
                              </Typography>
                            </Box>
                            <SendIcon fontSize="small" sx={{ transform: 'rotate(-45deg)' }} />
                          </Box>
                        )}
                        {message.message_type === 'location' && message.latitude && message.longitude && (
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" display="block">
                              📍 {message.location_name || 'Location'}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                              {message.latitude.toFixed(6)}, {message.longitude.toFixed(6)}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Message Input */}
            <Paper elevation={0} sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              {selectedFile && (
                <Box sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachFileIcon fontSize="small" />
                  <Typography variant="caption" sx={{ flex: 1 }}>
                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </Typography>
                  <IconButton size="small" onClick={() => setSelectedFile(null)}>
                    <Typography variant="caption">✕</Typography>
                  </IconButton>
                </Box>
              )}
              <Box component="form" onSubmit={handleSendMessage} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                />
                <IconButton size="small" onClick={() => fileInputRef.current?.click()}>
                  <AttachFileIcon />
                </IconButton>
                <TextField
                  fullWidth
                  size="small"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  variant="outlined"
                  disabled={uploading}
                />
                <IconButton
                  type="submit"
                  disabled={(!newMessage.trim() && !selectedFile) || uploading}
                  color="primary"
                  sx={{
                    bgcolor: 'success.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'success.dark' },
                    '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                  }}
                >
                  {uploading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                </IconButton>
              </Box>
            </Paper>
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'grey.50',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <PersonIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" fontWeight="600" color="text.secondary" gutterBottom>
                Select a conversation
              </Typography>
              <Typography color="text.disabled">
                Choose a conversation from the list to start messaging
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Messages;
